/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '@we-make-websites/theme-currency'

import { convertToHandle } from '~/helpers/convert'
import { isCdnUrl } from '~/helpers/general'
import { decodeApiId, transformEdges, transformMetaobjectFields } from '~/helpers/graphql'
import { getColorOptions, getStyleNumber } from '~/helpers/product'

/**
 * Format multi-store countries.
 * @param {Object} countries - Countries storefront API response.
 * @param {Array} data.instances - Shopify instances.
 * @param {Array} data.theCountries - Countries to be prefixed with 'the'.
 * @returns {Object}
 */
export function formatCountries(countries, { instances, theCountries } = {}) {
  const formattedCountries = {}

  /**
   * Find instance which has no countries assigned.
   */
  const unassignedInstance = instances.find((instance) => {
    return !instance.countries.length
  })

  /**
   * Format country response.
   */
  countries.localization.availableCountries.forEach((country) => {
    formattedCountries[country.code] = {
      country: {
        code: country.code.toUpperCase(),
        currency: country.currency?.code.toUpperCase(),
        currencySymbol: country.currency?.symbol,
        name: country.name,
        the: theCountries.includes(country.code),
      },
      instance: formatInstance(unassignedInstance),
      languages: country.availableLanguages.map((language) => {
        return {
          code: language.code.toUpperCase(),
          name: language.name,
        }
      }),
    }
  })

  /**
   * Add instance countries to list if they're not present in list.
   * - Ignores instances with no country codes set.
   */
  instances.forEach((instance) => {
    if (!instance.countries.length) {
      return
    }

    instance.countries.forEach((instanceCountry) => {
      formattedCountries[instanceCountry.code] = {
        ...formattedCountries[instanceCountry.code],
        instance: formatInstance(instance),
      }
    })
  })

  return formattedCountries
}

/**
 * Format TLD instance.
 * @param {Object} instance - Block setting instance object.
 * @returns {Object}
 */
function formatInstance(instance) {
  if (!instance) {
    return {
      url: false,
    }
  }

  /**
   * Ensure that URL ends with '/'.
   */
  const formattedMatchedUrl = instance.url.slice(-1) === '/'
    ? instance.url
    : `${instance.url}/`

  /**
   * Set URL to false if current store includes URL.
   * - URL is used to update /localization submit action so set to false if
   *   we want to use the default submit action.
   */
  let url = false
  const originWithoutWww = location.origin.replace('www.', '')

  if (
    !formattedMatchedUrl.includes('localhost') &&
    !formattedMatchedUrl.includes(location.origin) &&
    !formattedMatchedUrl.includes(originWithoutWww)
  ) {
    url = formattedMatchedUrl
  }

  /**
   * Return formatted object.
   */
  return {
    url,
  }
}

/**
 * Formats response from product.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response?.product

  if (!product) {
    return null
  }

  const price = product.b2bPriceBook?.product?.price || false
  const compareAtPrice = product.b2bPriceBook?.product?.compareAtPrice || false

  const formattedProduct = {
    ...product,
    available: product.available,
    collections: product.collections?.edges?.map((edge) => edge.node) || [],
    compareAtPrice: compareAtPrice || formatProductPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    handle: product.handle,
    id: decodeApiId(String(product.id)),
    images: formatImages(product),
    media: formatMedia(product),
    options: formatOptions(product),
    price: price || formatProductPrice(product.priceRange),
    tags: product.tags,
    title: formatProductTitle(product.title),
    originalTitle: product.title,
    type: product.type,
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    variants: formatVariants(product),
    vendor: product.vendor,
    metafields: formatMetafields(product),
    styleNumber: getStyleNumber(product),
    engraving: product.engraving,
    gemstones: JSON.parse(product.gemstones?.value || '[]'),
  }

  formattedProduct.colorOptions = getColorOptions(formattedProduct)

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))
  formattedProduct.featuredImage = Array.isArray(featuredImages)
    ? featuredImages[0]
    : featuredImages

  formattedProduct.secondaryImage = Array.isArray(featuredImages) && featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Formats product price.
 * @param {Object} price - Product price response.
 * @returns {Object}
 */
function formatProductPrice(price) {
  return {
    amount: Number(price?.minVariantPrice.amount) * 100 ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    max: Number(price?.maxVariantPrice.amount) * 100 ?? false,
    min: Number(price?.minVariantPrice.amount) * 100 ?? false,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }
}

/**
 * Formats images.
 * @param {Object} [product] - GraphQL response.
 * @param {Array} [images] - Custom array of images for formatting.
 * @returns {Array}
 */
function formatImages(product, images = false) {
  const imagesArray = images ? images : transformEdges(product?.images)

  if (!imagesArray.length) {
    return []
  }

  const formattedImages = imagesArray.map((image, index) => {
    if (!image?.url) {
      return {}
    }

    const linkedVariant = product?.variants?.length
      ? transformEdges(product.variants).find((variant) => {
          return variant.image.id === image.id
        })
      : false

    const alt = image.altText || ''
    if (product) {
      alt
        .replace(/#title#/gi, product.title)
        .replace(/#product-category#/gi, product.type)
    }
    return {
      alt,
      height: image.height,
      id: decodeApiId(image.id),
      index,
      src: image.url,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })

  if (images.length === 1) {
    return formattedImages[0]
  }

  return formattedImages
}

/**
 * Formats media.
 * @param {Object} product - GraphQL response.
 * @returns {Array}
 */
function formatMedia(product) {
  const mediaArray = transformEdges(product.media)

  if (!mediaArray.length) {
    return []
  }

  return mediaArray.map((media, index) => {
    const formattedMedia = {
      alt: media.alt,
      id: decodeApiId(media.id) || false,
      index,
      mediaType: media.mediaType.toLowerCase(),
    }

    switch (formattedMedia.mediaType) {
      case 'external_video':
        formattedMedia.embedUrl = media.embedUrl
        formattedMedia.host = media.host
        formattedMedia.previewImage = formatImages(false, [media.previewImage])
        break

      case 'image':
        formattedMedia.image = formatImages(false, [media.image])
        break

      case 'model_3d':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            mimeType: source.mimeType,
            url: source.url,
          }
        })
        break

      case 'video':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            height: source.height,
            mimeType: source.mimeType,
            url: source.url,
            width: source.width,
          }
        })
        break
    }

    return formattedMedia
  })
}

/**
 * Formats metafield values and removes null.
 * Matches Liquid metafield formats.
 * @param {Object} object - Parent object from GraphQL response.
 * @returns {[{key: string, value: any, namespace: string}]}
 */
function formatMetafields(object) {
  const metafields = []
  object.metafields?.forEach((meta) => {
    if (!meta) {
      return
    }

    const obj = {
      namespace: meta.namespace,
      key: meta.key,
    }

    let value

    if (meta.type.includes('list.metaobject_reference') && meta.references) {
      value = formatMetaobjectReferences(meta.references.nodes)
    } else if (meta.type === 'metaobject_reference') {
      value = {}
      meta.reference.fields.forEach((field) => {
        try {
          value[field.key] = JSON.parse(field.value)
        } catch {
          value[field.key] = field.value
        }
      })
    } else if (meta.type === 'file_reference' && meta.reference?.image) {
      value = formatImages(null, [meta.reference.image])
    } else {
      try {
        value = JSON.parse(meta.value)
      } catch {
        value = meta.value
      }
    }

    obj.value = value
    metafields.push(obj)
  })

  return metafields
}

/**
 * Format list of metaobject references
 * @param references - Shopify list of metaobject references
 */
function formatMetaobjectReferences(references) {
  return (references || []).map((entry) => {
    return entry.fields.reduce((obj, field) => {
      obj[field.key] = field.value
      return obj
    }, {})
  })
}

/**
 * Formats options.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options?.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Format a price amount.
 * - If the amount can be converted to a number, it is converted and multiplied
 *   by 100 to get the cents amount.
 * - Otherwise returns `false`.
 * @param {*} amount - Amount value to process.
 * @returns {Number|Boolean}
 */
export function formatPriceAmount(amount) {
  // eslint-disable-next-line no-undefined
  if (amount === null || amount === undefined) {
    return false
  }

  const numericAmount = Number(amount)

  if (isNaN(numericAmount)) {
    return false
  }

  return numericAmount * 100
}

/**
 * Remove everything after | character in product titles
 * @param {String} title
 * @param {String} [variantTitle] - The variant title to remove from the product
 * title.
 * @returns {String}
 */
export function formatProductTitle(title, variantTitle) {
  let newTitle = title?.split('|')[0]?.trim() || title

  if (variantTitle) {
    newTitle = newTitle.replace(`- ${variantTitle}`, '')
  }

  return newTitle
}

/**
 * Formats variants.
 * @param {Object} product - Product from Graphql response
 * @returns {Array}
 */
function formatVariants(product) {
  const b2bPriceBook = product.b2bPriceBook?.variants || []
  return transformEdges(product.variants).map((variant) => {
    const id = decodeApiId(variant.id)

    const b2bVariant = b2bPriceBook.find((b2bVariantPrice) => {
      return b2bVariantPrice.id === id
    })

    const price = b2bVariant?.price || false
    const compareAtPrice = b2bVariant?.compareAtPrice || false

    const formattedVariant = {
      ...variant,
      available: variant.available,
      compareAtPrice: compareAtPrice || formatVariantPrice(variant.compareAtPrice),
      id,
      image: decodeApiId(variant.image?.id) ?? false,
      imageObject: variant.image,
      inventory: variant.quantityAvailable,
      price: price || formatVariantPrice(variant.price),
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: `${option.name.charAt(0).toUpperCase()}${option.name.slice(1)}`,
          value: option.value,
        }
      }),
      metafields: formatMetafields(variant),
      sku: variant.sku,
    }

    /**
     * Wait for prices to be formatted before determining title.
     * - Gift card variants show price as their title for localisation purposes.
     */
    formattedVariant.title = product.giftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Checks if the currency needs to be explicitly stated.
 * @returns {String} The active currency if it matches the current country
 */
export function formatCurrencyCode() {
  const showCurrencies = window.cnvs?.metafields?.global?.showCurrencies || []
  const currentCountry = window.Shopify?.country.toLowerCase() || ''

  if (currentCountry && showCurrencies.includes(currentCountry)) {
    return cnvs.store.currency
  }
  return ''
}

/**
 * Formats variant price.
 * @param {Object} price - Variant price response.
 * @returns {Object}
 */
export function formatVariantPrice(price) {
  if (!price) {
    return {
      amount: false,
      currencyCode: false,
    }
  }

  return {
    amount: Number(price.amount) * 100,
    currencyCode: price.currencyCode,
  }
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    pageInfo: {
      ...collection.products?.pageInfo,
      currentPage: 1,
    },
    title: collection.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}`,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((product) => {
    return formatCollectionProduct(product)
  })

  return formattedCollection
}

/**
 * Format collection product response from collection or recommendations.
 * @param {Object} product - GraphQL response.
 * @param {Object} [collection] - Formatted collection response.
 * @returns {Object}
 */
export function formatCollectionProduct(product) {
  const url = `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`

  const formattedCollectionProduct = {
    available: product.available,
    compareAtPrice: formatProductPrice(product.compareAtPriceRange),
    handle: product.handle,
    price: formatProductPrice(product.priceRange),
    tags: product.tags,
    title: formatProductTitle(product.title),
    originalTitle: product.title,
    type: product.type,
    url,
    vendor: product.vendor,
    id: decodeApiId(product.id),
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))
  formattedCollectionProduct.featuredImage = featuredImages[0]

  formattedCollectionProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  return formattedCollectionProduct
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
          prefix,
          previousHandle: handle,
          response: object.items,
          tier: tier + 1,
        })
      : []

    const urlPath =
        object.url.split(cnvs.store.localisedPermanentDomain)[1] ||
        object.url.split(cnvs.store.permanentDomain)[1]
    const url = urlPath ? `//${location.host}${cnvs.routes.normalisedRoot}${urlPath}` : object.url

    return {
      banner: object?.resource?.menuBanner?.reference?.image ?? null,
      handle,
      links,
      tier,
      title: object.title,
      url,
    }
  })
}

/**
 * Format Shopify image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  if (!isCdnUrl(src)) {
    return src
  }

  const filetype = src.split('.').reverse()[0]?.split('?')[0]
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    return src
  }

  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove progressive filetype prefix.
   * - Remove filetype.
   * - Remove sizing, crop, and scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace('.progressive.', '.')
    .replace(`.${filetype}`, '.')
    .replace(/(?<size>_(?:\d+)?x(?:\d+)?)?(?<crop>_crop_(?:top|center|bottom|left|right))?(?<scale>@(?:2|3)x)?\./g, '.')

  url = `//${url}${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}

/**
 * Format size guide
 * @param {Object} response - sizeGuideMetaobject GraphQL response
 * @returns {Object}
 */
export function formatSizeGuide(response) {
  if (!response?.metaobject?.fields) {
    return null
  }

  return transformMetaobjectFields(response.metaobject.fields, ['internal_title'])
}
